import React from 'react';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';

// ui
import {Button, Card, Divider, FormLayout, Frame, Page, Toast} from '@shopify/polaris';
import PageSkeleton from '~/pages/common/PageSkeleton';
import {parseAxiosError} from '~/helpers/request';
import ApiClient from '~/client/ApiClient';
import {type AnySchema, InferType, object, string} from 'yup';
import TextInputField from '~/ui/form/TextInputField';

// images
import storeImage from '~/images/store-name.png';
import settingsImage from '~/images/shopify-settings.png';

export interface FormFields {
  store_name: string;
  api_key: string;
}

export const schema = object<FormFields, Record<keyof FormFields, AnySchema>>()
  .shape({
    store_name: string().required('Store name is required'),
    api_key: string().required('API key is required'),
  });

// open types
type FormValues = InferType<typeof schema>;

const Authorize: React.FC<React.PropsWithoutRef<any>> = () => {
  const apiClient = new ApiClient();

  const [initialValues, setInitialValues] = React.useState<FormFields>({store_name: '', api_key: ''});
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  const [toast, setToast] = React.useState<{ message: string; isError: boolean; isActive: boolean }>({
    message: '',
    isError: false,
    isActive: false,
  });

  // Function to toggle Toast
  const toggleToastActive = React.useCallback(() => setToast(prev => ({...prev, isActive: !prev.isActive})), []);

  // Toast Markup
  const toastMarkup = toast.isActive ? (
    <Toast content={toast.message} onDismiss={toggleToastActive} duration={5000} error={toast.isError}/>
  ) : null;

  React.useEffect(() => {
    setInitialValues({api_key: '', store_name: ''});
    // set once everything is done
    setIsLoading(false);
    setIsInitialized(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitHandler = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    if (!values.store_name.endsWith('.myshopify.com')) {
      formikHelpers.setErrors({store_name: 'Invalid shopify store url'});
      return;
    }

    setIsLoading(true);

    try {
      await apiClient.post('store/authorize', {
        storeDomain: values.store_name,
        apiKey: values.api_key,
      });

      setToast({
        message: 'Your store has been successfully authorized. redirecting...',
        isError: false,
        isActive: true,
      });

      setTimeout(() => {
        window.location.href = `https://admin.shopify.com/store/${values.store_name.split('.')[0]}/apps/flow`;
      }, 2000);
    } catch (e) {
      setToast({
        message: parseAxiosError(e),
        isError: true,
        isActive: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isInitialized) return <PageSkeleton/>;

  return (
    <Frame>
      {toastMarkup}
      <div className="authorize-page">
        <Page>
          <div className="form-header">
            <h2>Shopify Store Authorization</h2>
            <h4>Authorize shopify store domain using OLC Shopify API key</h4>
          </div>
        </Page>
        <div style={{width: '70%', marginLeft: 'auto', marginRight: 'auto'}}>
          <Card>
            <Formik<FormValues>
              initialValues={initialValues}
              enableReinitialize
              validationSchema={schema} validateOnMount validateOnBlur validateOnChange
              onSubmit={onSubmitHandler}>
              {(formik: FormikProps<FormValues>) => (
                <Form>
                  <FormLayout>
                    <TextInputField fieldInfo={{
                      type: 'text',
                      label: 'Store domain',
                      key: 'store_name',
                      required: true,
                      placeholder: 'my-store.myshopify.com',
                      helpText: <div style={{fontSize: 10}}>Shopify store domain: <em>my-store.myshopify.com</em></div>,
                      defaultValue: '',
                    }} formik={formik}/>

                    <TextInputField fieldInfo={{
                      type: 'text',
                      label: 'API key',
                      key: 'api_key',
                      required: true,
                      defaultValue: '',
                      placeholder: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
                      helpText: <div style={{fontSize: 10}}>To get your API key, Simply visit the <a
                        href="https://app.openletterconnect.com/" target="_blank"
                        rel="noreferrer">OpenLetterConnect.com</a>,<br/> Go to settings and Generate the Shopify API key
                        along with your Shopify store domain.</div>,
                    }} formik={formik}/>
                    <Button loading={isLoading} disabled={isLoading} submit>Authorize Store</Button>
                  </FormLayout>
                </Form>
              )}
            </Formik>
          </Card>
          <div style={{marginTop: 20, marginBottom: 20}}><Card>
            <div>
              <p style={{fontSize: '0.9rem', marginBottom: 5}}>How to find your correct Shopify store domain?</p>
              <p>- Log in to your Shopify store.</p>
              <p>- In the left-hand sidebar, scroll down and click Settings.</p>
              <p style={{marginTop: 10, marginBottom: 5}}><a href={settingsImage} target="_blank" rel="noreferrer" title="Click here to maximize">
                <img style={{width: 633}} src={settingsImage} alt=""/></a>
              </p>
              <p>- From the Settings menu, select Domains.</p>
              <p>- Locate your store name next to the primary domain and copy the store URL in the format:</p>
              <p style={{marginLeft: 10}}><code><strong style={{color: '#ED5C2F'}}><em>yourstorename</em></strong>.myshopify.com</code>
              </p>

              <p style={{marginTop: 10, marginBottom: 2}}>Please check the following image for a reference:</p>
              <p><a href={storeImage} target="_blank" rel="noreferrer" title="Click here to maximize">
                <img style={{width: 633}} src={storeImage} alt=""/></a>
              </p>
            </div>
          </Card>
          </div>
        </div>
      </div>
    </Frame>
  );
};

export default Authorize;
